import { useState, useEffect } from 'react';
import { useHistory } from "react-router-dom";

import { useFirebase } from '@meddbriefer/mdb-firebase';
import { saveDocument, verifyDocNameUniqueness } from "@meddbriefer/scenario-data"

const COLLECTION_ID = 'class-codes';

// separate non-exported helper component, potentially refactor as reusable in it's own file
const ButtonBar = ({allowChangeOrder=true, n, of, moveUpHandler, moveDownHandler, removeHandler}) => {
    return (
        <>
            <button
                className={allowChangeOrder ? "" : "hidden"}
                disabled={0 === n}
                onClick={() => moveUpHandler(n)}
            >
                ↑
            </button>
            <button
                className={allowChangeOrder ? "" : "hidden"}
                disabled={of === n}
                onClick={() => moveDownHandler(n)}
            >
                ↓
            </button>
           <button onClick={() => removeHandler(n)}> X </button>
        </>
    )
}

export default function ClassCode(props) {
    const {db} = useFirebase()
    const { classCode } = props.match.params;
    const [ scenarioVersions, setScenarioVersions ] = useState(null);
    // used for everything other than assignments
    const [ state, setState] = useState(null)
    const [ assignments, setAssignments ] = useState(null)
    const [ error, setError] = useState(null)
    let history = useHistory()

    const getScenarioVersions = (scen) => scenarioVersions[scen]

    const getUnassignedScenarios = () => {
        const allScens = Object.keys(scenarioVersions)
        const assignedScens = assignments.map(assign => assign.name)
        return allScens.filter(scen => !(assignedScens.includes(scen)))
    }

    const getAssignmentIndex = (scenName) => assignments.findIndex(rec => rec.name === scenName)

    const addAssignment = (scenName) => {
        setAssignments([
            ...assignments,
            { publishedScenarioID: "", name: scenName }
        ])
    }

    const removeAssignment = (idx) => {
        setAssignments([
            ...assignments.slice(0, idx),
            ...assignments.slice(idx + 1)
        ])
    }

    const updateAssignment = (idx, newRecord) => {
        setAssignments([
            ...assignments.slice(0, idx),
            newRecord,
            ...assignments.slice(idx + 1)
        ])
    }

    const swapAssignmentOrder = (idx1, idx2) => {
        const copy = assignments.slice()
        copy[idx1] = assignments[idx2]
        copy[idx2] = assignments[idx1]
        setAssignments(copy)
    }

    const handleMoveAssignmentUp = (currIdx) => swapAssignmentOrder(currIdx, currIdx-1)

    const handleMoveAssignmentDown = (currIdx) => swapAssignmentOrder(currIdx, currIdx+1)

    const handleChange = (event) => {
        const type = event.target.type
        const name = event.target.name
        const value = type === "checkbox" ? event.target.checked : event.target.value.trim()
        // console.log(type, name, value)
        setState(prevState => Object.assign({}, prevState, { [name]: value }))
    }

    const handleAssignScenario = (event) => addAssignment(event.target.value)

    const handleSelectScenarioVersion = (event, scenarioName) => {
        const value = event.target.value.trim()
        // console.log(scenarioName, value)
        const idx = getAssignmentIndex(scenarioName)
        // console.log(scenarioName, value, idx)
        if (-1 !== idx) {
            const newRecord = Object.assign({}, assignments[idx],  {publishedScenarioID: value})
            updateAssignment(idx, newRecord)
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        if ("" === state.name) {
            setError("Please specify a name for this class code")
            return
        } else if (!verifyDocNameUniqueness(db, COLLECTION_ID, state.name, "name", "V2")) {
            setError("class code name must be unique")
            return
        }
        assignments.forEach(assign => {
            if ("" === assign.publishedScenarioID) {
                setError(`please select which version of ${assign.name} you wish to use`)
            }
        })
        if (error) {
            return
        }
        const data = {
            id: classCode,
            name: state.name,
            orderRequired: state.orderRequired,
            assignments: assignments
        }

        await saveDocument(db, COLLECTION_ID, data);
        alert(`Assignments updated ${state.name}`);
        history.push('/class-codes-list/');
    }

    useEffect(() => {
        (async () => {
            // get list of published scenario versions and group then by scen name
            const scensAndVersions = {}
            const allScensAndVersions = await db.collection('published-scenarios').get()
            allScensAndVersions.forEach(doc => {
                // data is HUGE, we only need: id, name, datePublished, and version fields
                const {id, name, datePublished, version} = doc.data()

                if (!(name in scensAndVersions)) {
                    scensAndVersions[name] = []
                }
                scensAndVersions[name].push({id, datePublished, version})
            })
            setScenarioVersions(scensAndVersions)
        })()
    }, [])

    useEffect(() => {
        (async () => {
            if (scenarioVersions) {
                const cc = await db.collection(COLLECTION_ID).doc(classCode).get()
                const ccData = cc.data()
                setState({
                    name: ccData.name,
                    orderRequired: ccData.orderRequired,
                })
                // filter out unpublished scenarios
                let tmp = ccData.assignments.filter(assign => Object.keys(scenarioVersions).includes(assign.name))
                // set bad published versions IDs to "" to force either selection or removal by user
                tmp = tmp.map(assign => {
                    const copy = Object.assign({}, assign)
                    delete copy.order
                    // deal with legacy of field being id rather than publishedScenarioID
                    if (copy.id) {
                        copy.publishedScenarioID = copy.id
                        delete copy.id
                    }
                    const versions = scenarioVersions[assign.name].map(ver => ver.id)
                    if (!(versions.includes(assign.publishedScenarioID))) {
                        copy.publishedScenarioID = ""
                    }
                    return copy
                })
                setAssignments(tmp)
            }
        })()
    }, [db, classCode, scenarioVersions])

    useEffect(() => {
        !!assignments && console.log(assignments)
    }, [assignments])

    if (!assignments) {
        return (<div>Loading...</div>)
    }
    return (
        <div className="scenario-assignment">
            <h1>Class Code</h1>
            <label className="assignment-title">
                Name:
                <input type="text" name="name" value={state.name} onChange={handleChange} />
            </label>

            <table>
                <thead>
                    <tr><th>Scenario</th><th>Version</th><th></th></tr>
                </thead>
                <tbody>
                    {assignments.map((assign, assignIdx) =>
                    <tr key={assign.name}>
                        <td>{assign.name}</td>
                        <td>
                            <select value={assign.publishedScenarioID} onChange={(event) => handleSelectScenarioVersion(event, assign.name)}>
                                <option value=""> - - -</option>
                                {getScenarioVersions(assign.name).map(version =>
                                    <option key={version.id} value={version.id}>{version.version} ({version.datePublished}) </option>
                                )}
                            </select>
                        </td>
                        <td>
                            <ButtonBar
                                n={assignIdx}
                                of={assignments.length -1}
                                allowChangeOrder={state.orderRequired}
                                moveUpHandler={handleMoveAssignmentUp}
                                moveDownHandler={handleMoveAssignmentDown}
                                removeHandler={removeAssignment}
                            />
                        </td>
                    </tr>
                    )}
                </tbody>
            </table>
            <div>Add Scenario: <select onChange={handleAssignScenario}>
                <option value="">---</option>
                {getUnassignedScenarios().map(scen =>
                    <option key={scen}>{scen}</option>
                )}
            </select></div>
            <label className="required-bttn">
                Order required:
                <input
                    type="checkbox"
                    name="orderRequired"
                    checked={state.orderRequired}
                    onChange={handleChange}
                />
            </label>
            <button type="button" onClick={handleSubmit}>Submit</button>
        </div>
    );
};
